import { createUndoableCommit, type PendingCommit, type UndoableCommitOptions } from './undoableCommit';
import type { KeepaliveResult, UnloadRequest } from './undoableActionsCore';

/**
 * DQ-115 — a delayed commit (B.23, 6 s "ביטול") that survives the tab closing.
 *
 * When the page is hidden / left, an XHR (axios) sent then is cancelled by the
 * browser, so a waiting action would be lost ("החתמת יציאה" stayed clocked in).
 * `flushOnHide(send)` hands every waiting action that has an `unload` request to
 * the browser as a `fetch` with `keepalive` (the same sender as the P-27 deletes,
 * `makeKeepaliveSender`), and settles it with that request's outcome — never a
 * second request. An action without `unload`, or that the sender did not take
 * (`false`), is sent the normal way. Pure and framework-free.
 */
export interface UnloadAwareOptions<V, R> extends Omit<UndoableCommitOptions<V, R>, 'commit'> {
  commit: (vars: V) => Promise<R>;
  /** The same request as `commit`, as plain HTTP, for when the page goes away. */
  unload?: (vars: V) => UnloadRequest;
}

interface Box<V> {
  vars: V;
  /** Set on hide: the keepalive request already carries this action. */
  sent: Exclude<KeepaliveResult, false> | null;
}

export function createUnloadAwareCommit<V, R>(o: UnloadAwareOptions<V, R>) {
  const live = new Map<number, { box: Box<V>; pending: PendingCommit }>();
  const committer = createUndoableCommit<Box<V>, R>({
    windowMs: o.windowMs,
    timers: o.timers,
    commit: (box) => {
      if (box.sent !== null) return (box.sent === true ? Promise.resolve(undefined) : box.sent) as Promise<R>;
      return o.commit(box.vars);
    },
    onCommitted: (r, box, id) => { live.delete(id); o.onCommitted?.(r, box.vars, id); },
    onError: (e, box, id) => { live.delete(id); o.onError?.(e, box.vars, id); },
    onUndone: (box, id) => { live.delete(id); o.onUndone?.(box.vars, id); },
  });

  return {
    schedule(vars: V): PendingCommit {
      const box: Box<V> = { vars, sent: null };
      const pending = committer.schedule(box);
      live.set(pending.id, { box, pending });
      return pending;
    },
    /** The page is hidden / being left: send every waiting action now, by keepalive where possible. */
    flushOnHide(send: (req: UnloadRequest) => KeepaliveResult): Promise<void> {
      const waiting = [...live.values()].filter((a) => a.pending.state() === 'pending');
      return Promise.all(waiting.map(({ box, pending }) => {
        if (o.unload) {
          const r = send(o.unload(box.vars));
          if (r !== false) box.sent = r;
        }
        return pending.flush();
      })).then(() => undefined);
    },
    flushAll: () => committer.flushAll(),
    pendingCount: () => committer.pendingCount(),
    hasUnload: !!o.unload,
  };
}
